import React from "react";
import { toast } from "sonner";
import { CheckCircle2, Truck, Users, Handshake, Loader2 } from "lucide-react";
import useSeo from "../hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubcontractorPartnerPage() {
  useSeo({
    title: "Bliv vognmand-partner · EMA Kurer ApS",
    description:
      "Driver du eget vognmandsfirma på Sjælland? EMA Kurer søger pålidelige vognmænd til samarbejde. Faste opgaver, månedlig afregning, professionelt netværk.",
    path: "/vognmand-partner",
  });

  const [form, setForm] = React.useState({
    company_name: "", cvr: "", contact_name: "", contact_email: "", contact_phone: "",
    vehicle_count: "1", service_area: "", has_existing_business: true,
    motivation: "",
  });
  const [sent, setSent] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k) => setForm((f) => ({ ...f, [k]: !f[k] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_name || !form.contact_email || !form.contact_phone) {
      toast.error("Udfyld firmanavn, kontaktperson, email og telefon"); return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/business/subcontractor-inquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Server-fejl");
      }
      setSent(true);
      toast.success("Tak — vi vender tilbage hurtigst muligt");
    } catch (err) {
      toast.error(err.message || "Noget gik galt — prøv igen.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="subcontractor-partner-page">
      {/* Hero */}
      <section className="bg-brand-dark text-white py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="kicker text-brand-red">Vognmand-partnerskab</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 leading-[1.05] max-w-3xl">
            Driver du vognmandsfirma? Lad os arbejde sammen.
          </h1>
          <p className="text-white/70 mt-6 text-lg max-w-2xl leading-relaxed">
            EMA Kurer samarbejder med pålidelige vognmænd på Sjælland til same-day-, express- og aftenleveringer.
            Vi leverer ordrerne — du står for chaufførerne. Faste opgaver, månedlig afregning, professionelt netværk.
          </p>
        </div>
      </section>

      {/* Why partner */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16">
        <div className="kicker text-brand-red">Hvorfor samarbejde med os</div>
        <h2 className="font-display font-bold text-3xl sm:text-4xl text-brand-dark mt-3">3 grunde til at slutte sig til EMA-netværket</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
          <div className="border border-brand p-6 bg-white">
            <Truck className="text-brand-red" size={28} />
            <div className="font-display font-bold text-lg text-brand-dark mt-4">Stabil opgaveflow</div>
            <p className="text-brand-text2 text-sm mt-2 leading-relaxed">Vi har et fast og voksende kundegrundlag på Sjælland. Dine chauffører får dækket deres dag med faste leveringer i stedet for at jagte enkelt-jobs.</p>
          </div>
          <div className="border border-brand p-6 bg-white">
            <Users className="text-brand-red" size={28} />
            <div className="font-display font-bold text-lg text-brand-dark mt-4">Månedlig afregning</div>
            <p className="text-brand-text2 text-sm mt-2 leading-relaxed">Du modtager én samlet månedsafregning fra os — inkl. specificerede leveringer og automatisk betaling via bankoverførsel. Ingen tabskørsler ved tomme dage.</p>
          </div>
          <div className="border border-brand p-6 bg-white">
            <Handshake className="text-brand-red" size={28} />
            <div className="font-display font-bold text-lg text-brand-dark mt-4">Professionelt netværk</div>
            <p className="text-brand-text2 text-sm mt-2 leading-relaxed">Dine chauffører får adgang til EMA Kurer Driver-appen med rutestyring, GPS-tracking og digital leveringsbevis. Vi sørger for IT, marketing og kundeservice — du sørger for kørsel.</p>
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="kicker text-brand-red">Krav til vores partnere</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-brand-dark mt-3">Hvad vi forventer</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
            {[
              "Registreret virksomhed med CVR (vognmandsfirma, ApS eller enkeltmandsvirksomhed)",
              "Egne eller leasede køretøjer i god stand",
              "Alle chauffører har gyldigt kørekort og straffeattest",
              "Lovpligtig ansvarsforsikring",
              "Accept af EMA Kurer ApS' samarbejdsbetingelser",
              "Chauffører gennemfører vores onboarding-program (9 moduler + 70%-test)",
            ].map((req, i) => (
              <div key={i} className="flex items-start gap-3">
                <CheckCircle2 className="text-emerald-600 flex-shrink-0 mt-0.5" size={20} />
                <div className="text-brand-text text-sm leading-relaxed">{req}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-16">
        {sent ? (
          <div className="bg-white border border-brand p-10 text-center" data-testid="subcontractor-sent">
            <div className="w-14 h-14 bg-brand-red text-white mx-auto flex items-center justify-center"><CheckCircle2 size={28} /></div>
            <h2 className="font-display font-bold text-3xl text-brand-dark mt-6">Tak for jeres henvendelse!</h2>
            <p className="text-brand-text2 mt-3">Vi gennemgår alle henvendelser og vender tilbage hurtigst muligt — typisk inden for 1-3 hverdage på den email I har oplyst.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-brand p-7 sm:p-10 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="subcontractor-form">
            <div className="sm:col-span-2">
              <div className="kicker">Partnerskabs-henvendelse</div>
              <div className="font-display font-semibold text-2xl text-brand-dark mt-2">Fortæl os om jeres firma</div>
            </div>

            <div>
              <label className="label-flat">Firmanavn <span className="text-brand-red">*</span></label>
              <input className="input-flat" value={form.company_name} onChange={update("company_name")} data-testid="sub-company" required />
            </div>
            <div>
              <label className="label-flat">CVR-nummer</label>
              <input className="input-flat" value={form.cvr} onChange={update("cvr")} data-testid="sub-cvr" placeholder="8 cifre" />
            </div>

            <div>
              <label className="label-flat">Kontaktperson <span className="text-brand-red">*</span></label>
              <input className="input-flat" value={form.contact_name} onChange={update("contact_name")} data-testid="sub-contact-name" required />
            </div>
            <div>
              <label className="label-flat">Email <span className="text-brand-red">*</span></label>
              <input type="email" className="input-flat" value={form.contact_email} onChange={update("contact_email")} data-testid="sub-contact-email" required />
            </div>

            <div>
              <label className="label-flat">Telefon <span className="text-brand-red">*</span></label>
              <input className="input-flat" value={form.contact_phone} onChange={update("contact_phone")} data-testid="sub-contact-phone" required />
            </div>
            <div>
              <label className="label-flat">Antal køretøjer</label>
              <select className="input-flat" value={form.vehicle_count} onChange={update("vehicle_count")} data-testid="sub-vehicles">
                <option value="1">1 køretøj</option>
                <option value="2-5">2-5 køretøjer</option>
                <option value="5-10">5-10 køretøjer</option>
                <option value="10+">10+ køretøjer</option>
              </select>
            </div>

            <div className="sm:col-span-2">
              <label className="label-flat">Serviceområde (hvor I primært kører)</label>
              <input className="input-flat" value={form.service_area} onChange={update("service_area")} data-testid="sub-area" placeholder="Fx København, Roskilde, Helsingør, hele Sjælland" />
            </div>

            <div className="sm:col-span-2">
              <label className="flex items-center gap-2 text-sm font-medium text-brand-dark cursor-pointer">
                <input type="checkbox" checked={form.has_existing_business} onChange={() => toggle("has_existing_business")} data-testid="sub-has-existing" />
                Jeg har et eksisterende vognmandsfirma og kører allerede i dag
              </label>
            </div>

            <div className="sm:col-span-2">
              <label className="label-flat">Hvorfor vil I samarbejde med EMA Kurer? (valgfrit)</label>
              <textarea rows={4} className="input-flat" value={form.motivation} onChange={update("motivation")} data-testid="sub-motivation" placeholder="Fortæl kort om jeres kapacitet, erfaring og hvad I søger i et samarbejde" />
            </div>

            <div className="sm:col-span-2 mt-2">
              <button type="submit" disabled={busy} className="bg-brand-red text-white px-8 py-3 font-display font-semibold hover:bg-red-700 transition disabled:opacity-60 inline-flex items-center gap-2" data-testid="sub-submit">
                {busy ? <><Loader2 className="animate-spin" size={16} /> Sender...</> : <>Send henvendelse →</>}
              </button>
              <p className="text-xs text-brand-text2 mt-3">Ved at sende formular accepterer du, at vi behandler dine oplysninger i overensstemmelse med vores <a href="/privatlivspolitik" className="text-brand-red underline">privatlivspolitik</a>.</p>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
